'use client';

import { TIMELINE_FEED_VARIANT, type TimelineFeedVariant } from '@/config/feed';
import { useCustomFeed } from '@/hooks/useCustomFeed/useCustomFeed';
import { useIsMobile } from '@/hooks/useIsMobile/useIsMobile';
import { useHomeStore } from '@/stores/home/home.store';
import { LAYOUT, type LayoutType } from '@/stores/home/home.types';
import { pubkyLayoutToHomeLayout } from '@/utils/pubky-app-spec-feed-mappers';

export interface FeedLayoutResolutionInput {
  requestedLayout: LayoutType;
  variant: TimelineFeedVariant;
  isPhoneViewport: boolean;
}

export interface FeedLayoutResolution {
  requestedLayout: LayoutType;
  effectiveLayout: LayoutType;
  isCardsActive: boolean;
  isVisualRequested: boolean;
  isVisualActive: boolean;
  isPhoneViewport: boolean;
}

const RICH_LAYOUT_SUPPORTED_FEED_VARIANTS = new Set<TimelineFeedVariant>([
  TIMELINE_FEED_VARIANT.HOME,
  TIMELINE_FEED_VARIANT.CUSTOM,
  TIMELINE_FEED_VARIANT.SEARCH,
]);

export function resolveFeedLayout({
  requestedLayout,
  variant,
  isPhoneViewport,
}: FeedLayoutResolutionInput): FeedLayoutResolution {
  const isRichLayoutSupported = RICH_LAYOUT_SUPPORTED_FEED_VARIANTS.has(variant);
  const isCollectionVariant = variant === TIMELINE_FEED_VARIANT.COLLECTION;
  const isCardsRequested = requestedLayout === LAYOUT.CARDS;
  const isCardsSupported = isCollectionVariant || variant === TIMELINE_FEED_VARIANT.BOOKMARKS || isRichLayoutSupported;
  const isVisualRequested = requestedLayout === LAYOUT.VISUAL;
  const isVisualSupported = !isPhoneViewport && (isRichLayoutSupported || isCollectionVariant);
  const isWideRequested = requestedLayout === LAYOUT.WIDE;
  const isListRequested = requestedLayout === LAYOUT.LIST;
  const isListSupported = isRichLayoutSupported || isCollectionVariant;
  const effectiveLayout =
    (isCardsRequested && !isCardsSupported) ||
    (isVisualRequested && !isVisualSupported) ||
    (isWideRequested && !isRichLayoutSupported) ||
    (isListRequested && !isListSupported)
      ? isCollectionVariant
        ? LAYOUT.CARDS
        : LAYOUT.COLUMNS
      : requestedLayout;

  return {
    requestedLayout,
    effectiveLayout,
    isVisualRequested,
    isVisualActive: effectiveLayout === LAYOUT.VISUAL,
    isCardsActive: effectiveLayout === LAYOUT.CARDS,
    isPhoneViewport,
  };
}

export function useFeedLayoutResolution(
  variant: TimelineFeedVariant,
  requestedLayoutOverride?: LayoutType,
): FeedLayoutResolution {
  const homeLayout = useHomeStore((state) => state.layout);
  const customFeed = useCustomFeed();
  const isPhoneViewport = useIsMobile({ breakpoint: 'md' });
  const customFeedLayout =
    variant === TIMELINE_FEED_VARIANT.CUSTOM && customFeed?.layout !== undefined
      ? pubkyLayoutToHomeLayout(customFeed.layout)
      : undefined;

  const requestedLayout = requestedLayoutOverride ?? customFeedLayout ?? homeLayout;

  return resolveFeedLayout({
    requestedLayout,
    variant,
    isPhoneViewport,
  });
}
